// WorldHeatmapGeneric.jsx
import React, { useEffect } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5map from "@amcharts/amcharts5/map";
import am5geodata_worldLow from "@amcharts/amcharts5-geodata/worldLow";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";

/**
 * WorldHeatmapGeneric
 * Props:
 *  - facilityProfiles: array of objects with at least: site_name/name, location:{country, state_province, city, address, latitude?, longitude?}
 *  - mapId: optional unique id for the chart container (useful when rendering multiple maps)
 */
export default function WorldHeatmapGeneric({
  facilityProfiles = [],
  mapId = "chartdiv",
}) {
  useEffect(() => {
    // Create root and theme
    const root = am5.Root.new(mapId);
    root.setThemes([am5themes_Animated.new(root)]);

    // Create chart
    const chart = root.container.children.push(
      am5map.MapChart.new(root, {
        panX: "translateX",
        panY: "translateY",
        projection: am5map.geoMercator(),
      })
    );

    // Add zoom in/out buttons
    const zoomControl = am5map.ZoomControl.new(root, {});
    chart.set("zoomControl", zoomControl);
    // Position at top right corner
    zoomControl.setAll({
      centerX: am5.p100,
      centerY: am5.p0,
      x: am5.p100,
      y: 20, // distance from top
      marginRight: 20, // distance from right
    });

    // Base world polygons (country shapes)
    const polygonSeries = chart.series.push(
      am5map.MapPolygonSeries.new(root, {
        geoJSON: am5geodata_worldLow,
        exclude: ["AQ"],
      })
    );

    polygonSeries.mapPolygons.template.setAll({
      fill: am5.color(0xdff3f0),
      stroke: am5.color(0xffffff),
      strokeWidth: 0.6,
      tooltipText: "{name}",
      interactive: true,
    });

    // Point series - IMPORTANT: specify latitudeField & longitudeField
    const pointSeries = chart.series.push(
      am5map.MapPointSeries.new(root, {
        latitudeField: "latitude",
        longitudeField: "longitude",
      })
    );

    pointSeries.bullets.push(() => {
      const circle = am5.Circle.new(root, {
        radius: 7,
        tooltipText: "{name}\n{location}\n{approxText}",
        fill: am5.color(0xff3b30),
        fillOpacity: 0.95,
        stroke: am5.color(0xffffff),
        strokeWidth: 1,
      });
      circle.states.create("hover", { scale: 1.4 });
      return am5.Bullet.new(root, { sprite: circle });
    });

    // --- Prepare data: anchors = real coords that we can cluster around ---
    const entries = facilityProfiles || [];

    // Helper: check if valid numeric lat/lon
    const hasValidCoords = (f) =>
      f?.location &&
      Number.isFinite(Number(f.location.latitude)) &&
      Number.isFinite(Number(f.location.longitude));

    // Collect real points
    const realPoints = entries
      .map((f, idx) => {
        if (hasValidCoords(f)) {
          return {
            idx,
            name: f.site_name || f.name || `Site ${idx + 1}`,
            latitude: Number(f.location.latitude),
            longitude: Number(f.location.longitude),
            country: (f.location?.country || "")
              .toString()
              .toLowerCase()
              .trim(),
          };
        }
        return null;
      })
      .filter(Boolean);

    // Build quick lookup by country for real points
    const realByCountry = {};
    for (const rp of realPoints) {
      if (!realByCountry[rp.country]) realByCountry[rp.country] = [];
      realByCountry[rp.country].push(rp);
    }

    // Counters to deterministically jitter points around an anchor
    const anchorCounts = {}; // key -> count

    // deterministic jitter function based on an integer count
    const jitterAround = (count) => {
      // Spread radii and angles deterministically so the points don't stack
      // radius in degrees: start small and grow slightly with count
      const radius = 0.05 + count * 0.03; // ~0.05 to 0.2 degrees
      const angleDeg = (count * 137.5) % 360; // golden angle-ish
      const rad = (angleDeg * Math.PI) / 180;
      return {
        dLat: Math.cos(rad) * radius,
        dLon: Math.sin(rad) * radius,
      };
    };

    // Build point data for amCharts
    const pointData = entries.map((f, i) => {
      const loc = f.location || {};
      const locationText = [
        loc.address,
        loc.city,
        loc.state_province,
        loc.country,
      ]
        .filter(Boolean)
        .join(", ");
      const countryKey = (loc.country || "").toString().toLowerCase().trim();

      let latitude = Number(loc.latitude);
      let longitude = Number(loc.longitude);
      let approx = false;

      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        approx = true;

        // Strategy A: anchor to a real point in the SAME country (best)
        if (
          countryKey &&
          realByCountry[countryKey] &&
          realByCountry[countryKey].length > 0
        ) {
          const anchor =
            realByCountry[countryKey][i % realByCountry[countryKey].length]; // deterministic pick
          const key = `${anchor.latitude}_${anchor.longitude}`;
          const count = (anchorCounts[key] = (anchorCounts[key] || 0) + 1);
          const jitter = jitterAround(count);
          latitude = anchor.latitude + jitter.dLat;
          longitude = anchor.longitude + jitter.dLon;
        } else if (realPoints.length > 0) {
          // Strategy B: no real point in same country — anchor to a real point anywhere (round-robin)
          const anchor = realPoints[i % realPoints.length];
          const key = `${anchor.latitude}_${anchor.longitude}`;
          const count = (anchorCounts[key] = (anchorCounts[key] || 0) + 1);
          const jitter = jitterAround(count);
          latitude = anchor.latitude + jitter.dLat;
          longitude = anchor.longitude + jitter.dLon;
        } else {
          // Strategy C: no real points anywhere — deterministic global spread (avoid 0,0 cluster)
          // This keeps points visible and spread across the globe.
          latitude = -50 + ((i * 37) % 100); // in [-50, +50]
          longitude = -170 + ((i * 97) % 340); // in [-170, +170]
        }
      }

      return {
        name: f.site_name || f.name || `Site ${i + 1}`,
        location: locationText || "—",
        latitude,
        longitude,
        approxText: approx ? "(approx.)" : "",
      };
    });

    // Finally set data AFTER the series is configured
    pointSeries.data.setAll(pointData);

    chart.appear(800, 200);

    // Hide the AmCharts logo
    root._logo?.dispose();

    // cleanup
    return () => {
      root.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityProfiles, mapId]);

  return (
    <div
      id={mapId}
      style={{
        width: "100%",
        height: "500px",
        borderRadius: "8px",
        overflow: "hidden",
      }}
    />
  );
}
